import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Loader2,
  Play,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Eyebrow } from '@/components/ui/eyebrow';
import { Pill } from '@/components/ui/pill';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Empty } from '@/components/ui/empty';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import { useAcordos } from '@/lib/hooks/entidades';
import {
  useDebouncedValue,
  usePreview,
  useCreatePrestacaoFromPreview,
  type CategoriaSnapshot,
  type PreviewSnapshot,
} from '@/lib/hooks/prestacoes';
import { CategoriaProgressBar } from '@/features/prestacoes/CategoriaProgressBar';
import { DrillDownDrawer } from '@/features/prestacoes/DrillDownDrawer';
import { fmtBRL } from '@/lib/utils';

export const Route = createFileRoute('/_app/prestacoes/preview')({
  component: PreviewPrestacaoPage,
});

// ─── Schema de filtro ─────────────────────────────────────────────────────────

const filterSchema = z.object({
  acordoId: z.string().min(1, 'Selecione um convênio'),
  competencia: z
    .string()
    .regex(/^\d{4}-\d{2}$/, 'Formato YYYY-MM')
    .min(1, 'Informe a competência'),
});
type FilterValues = z.infer<typeof filterSchema>;

function defaultCompetencia(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// ─── Snapshot card clicável ───────────────────────────────────────────────────

interface SnapshotCardProps {
  label: string;
  value: number;
  hint?: string;
  onClick?: () => void;
  disabled?: boolean;
}

function SnapshotCard({ label, value, hint, onClick, disabled }: SnapshotCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="group rounded-2 border border-card-stroke bg-card-soft p-3 text-left transition-all duration-fast hover:border-mint-deep/40 hover:bg-mint-soft/30 hover:shadow-1 disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mint-deep focus-visible:ring-offset-2"
      aria-label={`Ver detalhes de ${label}: ${value}`}
    >
      <Eyebrow>{label}</Eyebrow>
      <p className="font-mono text-[22px] font-bold text-fg tabular-nums mt-1">{value}</p>
      {hint && <p className="text-[11px] text-fg-muted font-mono mt-0.5">{hint}</p>}
      {onClick && (
        <div className="flex items-center gap-1 mt-1.5 text-[11px] text-mint-deep opacity-0 group-hover:opacity-100 transition-opacity">
          Ver detalhes
          <ChevronRight className="h-3 w-3" strokeWidth={2} />
        </div>
      )}
    </button>
  );
}

function SnapshotCardSkeleton() {
  return (
    <div className="rounded-2 border border-card-stroke bg-card-soft p-3 space-y-1.5" aria-hidden>
      <div className="skel h-2 w-16 rounded-1" />
      <div className="skel h-6 w-10 rounded-1" />
      <div className="skel h-2 w-24 rounded-1" />
    </div>
  );
}

// ─── Conciliação badge ────────────────────────────────────────────────────────

const CONCIL_TONE: Record<string, 'mint' | 'warn' | 'block'> = {
  OK: 'mint',
  WARN: 'warn',
  BLOCK: 'block',
};

const CONCIL_LABEL: Record<string, string> = {
  OK: 'Conciliada',
  WARN: 'Atenção',
  BLOCK: 'Divergência',
};

function ConciliacaoCard({
  conciliacao,
}: {
  conciliacao: PreviewSnapshot['conciliacao'];
}) {
  return (
    <div className="rounded-2 border border-card-stroke bg-card-soft p-3 flex flex-col gap-1.5">
      <Eyebrow>Conciliação</Eyebrow>
      <Pill tone={CONCIL_TONE[conciliacao.status] ?? 'default'} dot className="w-fit">
        {CONCIL_LABEL[conciliacao.status] ?? conciliacao.status}
      </Pill>
      {conciliacao.descricao && (
        <p className="text-[12px] text-fg-muted leading-snug">{conciliacao.descricao}</p>
      )}
    </div>
  );
}

// ─── Issues section ───────────────────────────────────────────────────────────

function IssuesSection({
  issues,
}: {
  issues: Array<{ severity: 'block' | 'warn'; code: string; message: string }>;
}) {
  const blocks = issues.filter((i) => i.severity === 'block');
  const warns = issues.filter((i) => i.severity === 'warn');

  if (issues.length === 0) {
    return (
      <div className="rounded-3 border border-mint-deep/20 bg-mint-soft/40 p-4 flex items-center gap-2">
        <CheckCircle2 className="h-5 w-5 text-mint-deep shrink-0" strokeWidth={1.8} />
        <p className="text-sm text-mint-deep font-medium">
          Tudo pronto — sem bloqueios nem alertas.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {blocks.length > 0 && (
        <div
          className="rounded-3 border border-block/30 bg-block-soft/40 p-4"
          role="alert"
          aria-live="polite"
        >
          <div className="flex items-start gap-2 mb-2">
            <AlertCircle className="h-4 w-4 text-block shrink-0 mt-0.5" strokeWidth={1.8} />
            <p className="text-sm font-semibold text-block">
              {blocks.length} bloqueio{blocks.length !== 1 ? 's' : ''} — corrija antes de iniciar
            </p>
          </div>
          <ul className="space-y-1.5 ml-6" aria-label="Lista de bloqueios">
            {blocks.map((b) => (
              <li key={b.code} className="text-sm text-fg flex gap-2">
                <span className="text-block" aria-hidden>
                  ·
                </span>
                <span>{b.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {warns.length > 0 && (
        <div className="rounded-3 border border-warn/30 bg-warn-soft/40 p-4">
          <div className="flex items-start gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-warn shrink-0 mt-0.5" strokeWidth={1.8} />
            <p className="text-sm font-semibold text-warn">
              {warns.length} alerta{warns.length !== 1 ? 's' : ''} — vale revisar
            </p>
          </div>
          <ul className="space-y-1.5 ml-6" aria-label="Lista de alertas">
            {warns.map((w) => (
              <li key={w.code} className="text-sm text-fg-muted flex gap-2">
                <span aria-hidden>·</span>
                <span>{w.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

type DrillKind =
  | 'pagamentos'
  | 'receitas'
  | 'empregados'
  | 'bens'
  | 'contratos'
  | 'documentos-fiscais'
  | null;

interface DrillState {
  kind: DrillKind;
  title: string;
}

function PreviewPrestacaoPage() {
  const { user } = useAuth();
  const workspaceId = user?.effectiveWorkspaceId ?? null;

  const {
    register,
    watch,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FilterValues>({
    resolver: zodResolver(filterSchema),
    defaultValues: { acordoId: '', competencia: defaultCompetencia() },
  });

  const rawAcordoId = watch('acordoId');
  const rawCompetencia = watch('competencia');

  // Debounce 400ms — competência é digitada, acordoId é select (instantâneo)
  const acordoId = useDebouncedValue(rawAcordoId, 400);
  const competencia = useDebouncedValue(rawCompetencia, 400);

  const [submitted, setSubmitted] = useState(false);
  const [drill, setDrill] = useState<DrillState>({ kind: null, title: '' });

  const acordosQ = useAcordos(workspaceId);
  const acordos = acordosQ.data?.items ?? [];

  const previewQ = usePreview(workspaceId, acordoId, competencia, {
    enabled: submitted && !!acordoId && /^\d{4}-\d{2}$/.test(competencia),
  });

  const createMut = useCreatePrestacaoFromPreview(workspaceId);

  const snapshot = previewQ.data?.snapshot;
  const issues = previewQ.data?.issues ?? [];
  const previewToken = previewQ.data?.previewToken ?? '';
  const tokenExpiresAt = previewQ.data?.tokenExpiresAt;

  const hasBlocks = issues.some((i) => i.severity === 'block');
  const isTokenExpired = tokenExpiresAt ? new Date(tokenExpiresAt) < new Date() : false;

  function openDrill(kind: DrillKind, title: string) {
    setDrill({ kind, title });
  }

  function closeDrill() {
    setDrill({ kind: null, title: '' });
  }

  function onSubmitFilter() {
    setSubmitted(true);
  }

  async function handleIniciarPrestacao() {
    if (!acordoId || !competencia || !previewToken) return;

    // Se token expirou, força refetch antes de prosseguir
    if (isTokenExpired) {
      toast.error(
        'Os dados foram atualizados desde a sua prévia. Recarregue para ver os números atuais.',
      );
      previewQ.refetch();
      return;
    }

    try {
      await createMut.mutateAsync({ acordoId, competencia, previewToken });
      toast.success('Prestação iniciada com sucesso!');
    } catch {
      // Erros 409 e 403 tratados no hook onError
      // Refetch automático após 409 SNAPSHOT_STALE
      if ((createMut.error as { status?: number })?.status === 409) {
        previewQ.refetch();
      }
    }
  }

  const selectedAcordo = acordos.find((a) => a.id === acordoId);
  const competenciaLabel = competencia
    ? competencia.split('-').reverse().join('/')
    : '';

  if (!workspaceId) {
    return (
      <Card variant="default" className="p-8 text-center">
        <p className="text-sm text-fg-muted">Selecione um workspace para usar esta funcionalidade.</p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* ── Hero ──────────────────────────────────────────────────────────────── */}
      <Card variant="mint" className="p-6 md:p-7">
        <Eyebrow className="opacity-70">Pré-visualização</Eyebrow>
        <h1 className="font-display text-[26px] md:text-[30px] font-bold tracking-[-0.025em] leading-[1.05] mt-2 mb-1">
          Conferir antes de enviar
        </h1>
        <p className="text-sm opacity-75">
          Veja o consolidado do mês, verifique os bloqueios e inicie a prestação com segurança.
        </p>
      </Card>

      {/* ── Formulário de filtro ──────────────────────────────────────────────── */}
      <Card variant="default" className="p-5">
        <form onSubmit={handleSubmit(onSubmitFilter)} noValidate>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Convênio */}
            <div>
              <Label htmlFor="acordoId" className="mb-1.5 block">
                Convênio
              </Label>
              <Controller
                control={control}
                name="acordoId"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="acordoId" aria-describedby="acordoId-err">
                      <SelectValue placeholder="Selecione o convênio…" />
                    </SelectTrigger>
                    <SelectContent>
                      {acordos.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.numero} — {a.orgaoConcedente}
                        </SelectItem>
                      ))}
                      {acordos.length === 0 && (
                        <div className="px-3 py-2 text-xs text-fg-muted">
                          Cadastre um convênio em Configurações primeiro.
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.acordoId && (
                <p id="acordoId-err" role="alert" className="text-[11px] text-warn mt-1">
                  {errors.acordoId.message}
                </p>
              )}
            </div>

            {/* Competência */}
            <div>
              <Label htmlFor="competencia" className="mb-1.5 block">
                Competência
              </Label>
              <Input
                id="competencia"
                type="month"
                aria-describedby="competencia-err"
                {...register('competencia')}
              />
              {errors.competencia && (
                <p id="competencia-err" role="alert" className="text-[11px] text-warn mt-1">
                  {errors.competencia.message}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 mt-4">
            <Button type="submit" variant="primary" size="sm">
              {previewQ.isFetching ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                  Carregando…
                </>
              ) : (
                'Pre-visualizar'
              )}
            </Button>

            {submitted && snapshot && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => previewQ.refetch()}
                disabled={previewQ.isFetching}
                aria-label="Atualizar prévia"
              >
                <RefreshCw
                  className={`h-4 w-4 ${previewQ.isFetching ? 'animate-spin' : ''}`}
                  strokeWidth={1.8}
                />
                Atualizar
              </Button>
            )}
          </div>
        </form>
      </Card>

      {/* ── Skeletons de loading ──────────────────────────────────────────────── */}
      {previewQ.isFetching && !snapshot && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[...Array(6)].map((_, i) => (
              <SnapshotCardSkeleton key={i} />
            ))}
          </div>
          <div className="rounded-3 border border-card-stroke bg-card-soft h-16 animate-pulse" aria-hidden />
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="rounded-2 border border-card-stroke bg-card-soft h-20 animate-pulse" aria-hidden />
            ))}
          </div>
        </div>
      )}

      {/* ── Preview resultado ─────────────────────────────────────────────────── */}
      {snapshot && (
        <>
          {/* Token expirado */}
          {isTokenExpired && (
            <div
              className="rounded-3 border border-warn/30 bg-warn-soft/40 p-4 flex items-center gap-3"
              role="alert"
              aria-live="polite"
            >
              <AlertTriangle className="h-5 w-5 text-warn shrink-0" strokeWidth={1.8} />
              <p className="text-sm text-warn">
                Os dados foram atualizados desde a sua prévia. Recarregue para ver os números
                atuais.
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="ml-auto"
                onClick={() => previewQ.refetch()}
              >
                Recarregar
              </Button>
            </div>
          )}

          {/* Cards de snapshot */}
          <section aria-label="Resumo do mês">
            <Eyebrow className="mb-3 block">Resumo do mes — {competenciaLabel}</Eyebrow>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <SnapshotCard
                label="Pagamentos"
                value={snapshot.pagamentos.count}
                hint={fmtBRL(snapshot.pagamentos.total)}
                onClick={() =>
                  openDrill(
                    'pagamentos',
                    `Pagamentos — ${competenciaLabel} · ${selectedAcordo?.numero ?? ''}`,
                  )
                }
              />
              <SnapshotCard
                label="Receitas"
                value={snapshot.receitas.count}
                hint={fmtBRL(snapshot.receitas.total)}
                onClick={() =>
                  openDrill(
                    'receitas',
                    `Receitas — ${competenciaLabel} · ${selectedAcordo?.numero ?? ''}`,
                  )
                }
              />
              <SnapshotCard
                label="Empregados ativos"
                value={snapshot.empregados.ativos}
                onClick={() =>
                  openDrill(
                    'empregados',
                    `Empregados — ${competenciaLabel} · ${selectedAcordo?.numero ?? ''}`,
                  )
                }
              />
              <SnapshotCard
                label="Bens"
                value={snapshot.bens.count}
                onClick={() =>
                  openDrill(
                    'bens',
                    `Bens — ${competenciaLabel} · ${selectedAcordo?.numero ?? ''}`,
                  )
                }
              />
              <SnapshotCard
                label="Contratos"
                value={snapshot.contratos.count}
                onClick={() =>
                  openDrill(
                    'contratos',
                    `Contratos — ${competenciaLabel} · ${selectedAcordo?.numero ?? ''}`,
                  )
                }
              />
              <SnapshotCard
                label="Doc. Fiscais"
                value={snapshot.documentosFiscais.count}
                onClick={() =>
                  openDrill(
                    'documentos-fiscais',
                    `Documentos Fiscais — ${competenciaLabel} · ${selectedAcordo?.numero ?? ''}`,
                  )
                }
              />
            </div>
          </section>

          {/* Conciliação */}
          <section aria-label="Status de conciliação">
            <Eyebrow className="mb-3 block">Conciliacao bancaria</Eyebrow>
            <ConciliacaoCard conciliacao={snapshot.conciliacao} />
          </section>

          {/* Categorias */}
          {snapshot.categorias.length > 0 && (
            <section aria-label="Categorias orçamentárias">
              <Eyebrow className="mb-3 block">Categorias orcamentarias</Eyebrow>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {snapshot.categorias.map((cat: CategoriaSnapshot) => (
                  <CategoriaProgressBar key={cat.slug} categoria={cat} />
                ))}
              </div>
            </section>
          )}

          {/* Issues */}
          <section aria-label="Pendências e bloqueios">
            <Eyebrow className="mb-3 block">Pendencias e bloqueios</Eyebrow>
            <IssuesSection issues={issues} />
          </section>

          {/* Botão iniciar */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2 pb-4">
            <Button
              type="button"
              variant="primary"
              size="lg"
              disabled={hasBlocks || createMut.isPending || isTokenExpired}
              onClick={handleIniciarPrestacao}
              aria-disabled={hasBlocks}
              aria-describedby={hasBlocks ? 'iniciar-blocked-hint' : undefined}
              className="flex-1 sm:flex-none"
            >
              {createMut.isPending ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.8} />
                  Iniciando…
                </>
              ) : (
                <>
                  <Play className="h-5 w-5" strokeWidth={1.8} />
                  Iniciar Prestacao
                </>
              )}
            </Button>

            {hasBlocks && (
              <p
                id="iniciar-blocked-hint"
                className="text-xs text-block flex items-center gap-1.5"
                role="status"
              >
                <AlertCircle className="h-3.5 w-3.5 shrink-0" strokeWidth={1.8} />
                Corrija os bloqueios acima para habilitar o envio.
              </p>
            )}
          </div>
        </>
      )}

      {/* Empty state — antes de submeter */}
      {!submitted && !previewQ.isFetching && (
        <Empty
          icon={ClipboardList}
          eyebrow="Pre-visualizacao"
          title="Selecione o convênio e o mês"
          description="Escolha o convênio e a competência acima e clique em Pre-visualizar para conferir os dados antes de enviar."
          variant="soft"
        />
      )}

      {/* Erro de rede */}
      {previewQ.isError && submitted && (
        <div
          className="rounded-3 border border-block/30 bg-block-soft/40 p-5 flex items-start gap-3"
          role="alert"
        >
          <AlertCircle className="h-5 w-5 text-block shrink-0 mt-0.5" strokeWidth={1.8} />
          <div>
            <p className="text-sm font-semibold text-block">Nao foi possivel carregar a previa</p>
            <p className="text-sm text-fg-muted mt-0.5">
              {previewQ.error instanceof Error
                ? previewQ.error.message
                : 'Verifique sua conexão e tente novamente.'}
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="mt-3"
              onClick={() => previewQ.refetch()}
            >
              <RefreshCw className="h-4 w-4" strokeWidth={1.8} />
              Tentar novamente
            </Button>
          </div>
        </div>
      )}

      {/* ── Drill-down drawer ─────────────────────────────────────────────────── */}
      {drill.kind && acordoId && competencia && (
        <DrillDownDrawer
          open={!!drill.kind}
          onOpenChange={(v) => !v && closeDrill()}
          workspaceId={workspaceId}
          kind={drill.kind}
          acordoId={acordoId}
          competencia={competencia}
          title={drill.title}
        />
      )}
    </div>
  );
}
